import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { subDays } from 'date-fns';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { userProfileService, workoutPlanService } from '../../services/supabaseService';
import { predictGoalAchievement, identifyAtRiskClients, generateInterventionRecommendations } from '../../services/anthropicService';
import PredictionGaugeCard from './components/PredictionGaugeCard';
import RiskAlertCard from './components/RiskAlertCard';
import InterventionPanel from './components/InterventionPanel';
import PredictiveTrendChart from './components/PredictiveTrendChart';

const PredictiveAnalyticsDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [assignedWorkouts, setAssignedWorkouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [interventions, setInterventions] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30days');

  useEffect(() => {
    if (userProfile?.role !== 'personal_trainer') {
      navigate('/dashboard');
      return;
    }
    loadDashboardData();
  }, [user, userProfile]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const { data: clientsList } = await userProfileService?.getTrainerClients(user?.id);
      setClients(clientsList || []);

      const { data: workouts } = await workoutPlanService?.getAssignedWorkouts(user?.id, 'personal_trainer');
      setAssignedWorkouts(workouts || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDateRangeFilter = () => {
    const today = new Date();
    switch (dateRange) {
      case '7days':
        return subDays(today, 7);
      case '30days':
        return subDays(today, 30);
      case '90days':
        return subDays(today, 90);
      default:
        return subDays(today, 30);
    }
  };

  const filteredWorkouts = assignedWorkouts?.filter(w => {
    const scheduledDate = new Date(w?.scheduledDate);
    const rangeStart = getDateRangeFilter();
    return scheduledDate >= rangeStart;
  });

  const calculateClientStats = (clientId) => {
    const clientWorkouts = filteredWorkouts?.filter(w => w?.clientId === clientId);
    const totalWorkouts = clientWorkouts?.length;
    const completedWorkouts = clientWorkouts?.filter(w => w?.status === 'completed')?.length;
    const missedWorkouts = clientWorkouts?.filter(w => w?.status === 'missed' || w?.status === 'skipped')?.length;
    const adherenceRate = totalWorkouts > 0 ? Math.round((completedWorkouts / totalWorkouts) * 100) : 0;

    const sortedCompleted = [...clientWorkouts]
      ?.filter(w => w?.status === 'completed')
      ?.sort((a, b) => new Date(b?.completedAt) - new Date(a?.completedAt));

    let missedStreak = 0;
    const recentWorkouts = [...clientWorkouts]
      ?.sort((a, b) => new Date(b?.scheduledDate) - new Date(a?.scheduledDate))
      ?.slice(0, 5);
    
    for (const workout of recentWorkouts) {
      if (workout?.status === 'missed' || workout?.status === 'skipped') {
        missedStreak++;
      } else if (workout?.status === 'completed') {
        break;
      }
    }

    const lastActivity = sortedCompleted?.[0]?.completedAt;
    const lastActivityDate = lastActivity ? new Date(lastActivity)?.toLocaleDateString() : null;

    const olderWorkouts = clientWorkouts?.slice(0, Math.floor(clientWorkouts?.length / 2));
    const recentHalf = clientWorkouts?.slice(Math.floor(clientWorkouts?.length / 2));
    const olderAdherence = olderWorkouts?.length > 0 
      ? (olderWorkouts?.filter(w => w?.status === 'completed')?.length / olderWorkouts?.length) * 100
      : 0;
    const recentAdherence = recentHalf?.length > 0
      ? (recentHalf?.filter(w => w?.status === 'completed')?.length / recentHalf?.length) * 100
      : 0;
    const adherenceTrend = recentAdherence > olderAdherence + 10 ? 'improving' 
      : recentAdherence < olderAdherence - 10 ? 'declining' :'stable';

    return {
      totalWorkouts,
      completedWorkouts,
      missedWorkouts,
      adherenceRate,
      missedWorkoutStreak: missedStreak,
      lastActivityDate,
      adherenceTrend,
      recentWorkouts
    };
  };

  const enrichedClients = clients?.map(client => ({
    ...client,
    stats: calculateClientStats(client?.id)
  }));

  const handlePredictGoalAchievement = async (client) => {
    setIsAnalyzing(true);
    setError(null);
    setSelectedClient(client);
    setPredictions(null);

    try {
      const stats = client?.stats;
      const clientWorkouts = filteredWorkouts?.filter(w => w?.clientId === client?.id);
      
      const clientData = {
        clientName: client?.fullName,
        goalType: client?.fitnessGoals || client?.healthGoals || 'General fitness improvement',
        targetDate: 'Next 90 days',
        currentProgress: stats?.adherenceRate,
        historicalAdherence: stats?.adherenceRate,
        workoutHistory: clientWorkouts?.map(w => ({
          workoutName: w?.workoutPlanName || 'Workout',
          status: w?.status,
          completedAt: w?.completedAt,
          scheduledDate: w?.scheduledDate
        })),
        baselineMetrics: {
          'Total Workouts': stats?.totalWorkouts,
          'Completed': stats?.completedWorkouts,
          'Current Trend': stats?.adherenceTrend
        },
        progressRate: stats?.adherenceTrend
      };

      const result = await predictGoalAchievement(clientData);

      if (result?.success) {
        setPredictions(result?.prediction);
      } else {
        setError(result?.error || 'Failed to generate prediction');
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleIdentifyAtRiskClients = async () => {
    setIsAnalyzing(true);
    setError(null);
    setRiskAssessment(null);

    try {
      const clientsData = enrichedClients?.map(client => ({
        fullName: client?.fullName,
        adherenceRate: client?.stats?.adherenceRate,
        adherenceTrend: client?.stats?.adherenceTrend,
        missedWorkoutStreak: client?.stats?.missedWorkoutStreak,
        lastActivityDate: client?.stats?.lastActivityDate
      }));

      const result = await identifyAtRiskClients(clientsData);

      if (result?.success) {
        setRiskAssessment(result?.riskAssessment);
      } else {
        setError(result?.error || 'Failed to identify at-risk clients');
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateIntervention = async (client) => {
    setIsAnalyzing(true);
    setError(null);
    setSelectedClient(client);
    setInterventions(null);

    try {
      const stats = client?.stats;
      const riskLevel = stats?.adherenceRate < 40 ? 'High' 
        : stats?.adherenceRate < 70 ? 'Medium' :'Low';

      const clientData = {
        clientName: client?.fullName,
        riskLevel,
        primaryConcerns: stats?.adherenceTrend === 'declining' ?'Declining adherence pattern' 
          : stats?.missedWorkoutStreak > 2 
          ? `${stats?.missedWorkoutStreak} consecutive missed workouts`
          : 'Low engagement',
        adherenceRate: stats?.adherenceRate,
        missedWorkoutStreak: stats?.missedWorkoutStreak,
        lastContactDate: stats?.lastActivityDate,
        workoutPreferences: client?.fitnessGoals,
        barriers: stats?.adherenceTrend === 'declining' ? 'Possible motivation or scheduling issues' : null
      };

      const result = await generateInterventionRecommendations(clientData);

      if (result?.success) {
        setInterventions(result?.interventions);
      } else {
        setError(result?.error || 'Failed to generate interventions');
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleMessageClient = (clientId) => {
    navigate('/communication-hub', { state: { selectedClientId: clientId } });
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading predictive analytics...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Predictive Analytics - FormForge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Predictive Analytics</h1>
              <p className="mt-2" style={{ color: '#6B7280' }}>AI-powered client performance forecasting and risk identification</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" iconName="RefreshCw" onClick={loadDashboardData}>Refresh Data</Button>
              <Button variant="default" iconName="Brain" onClick={handleIdentifyAtRiskClients} disabled={isAnalyzing}>
                {isAnalyzing ? 'Analyzing...' : 'Identify At-Risk Clients'}
              </Button>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium" style={{ color: '#E8EAED' }}>Analysis Period:</span>
              <div className="flex gap-2">
                {['7days', '30days', '90days']?.map(range => (
                  <button key={range} onClick={() => setDateRange(range)} className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all" style={dateRange === range ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {range === '7days' ? '7 Days' : range === '30days' ? '30 Days' : '90 Days'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.2)' }}>
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} style={{ color: '#FF4444' }} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#FF4444' }}>Analysis Error</p>
                  <p className="text-sm mt-1" style={{ color: 'rgba(255,68,68,0.8)' }}>{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Risk Assessment Panel */}
          {riskAssessment && (
            <div className="rounded-xl p-6 mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,107,0,0.2)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,107,0,0.1)' }}>
                  <Icon name="AlertTriangle" size={20} style={{ color: '#FF6B00' }} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold" style={{ color: '#E8EAED' }}>At-Risk Client Assessment</h2>
                  <p className="text-sm" style={{ color: '#6B7280' }}>AI-identified clients requiring intervention</p>
                </div>
              </div>
              <div className="whitespace-pre-line" style={{ color: '#6B7280' }}>
                {riskAssessment?.split('\n\n')?.map((section, idx) => <p key={idx} className="mb-3">{section}</p>)}
              </div>
            </div>
          )}

          {/* Prediction Gauges Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {enrichedClients?.slice(0, 4)?.map(client => (
              <PredictionGaugeCard key={client?.id} client={client} onPredict={() => handlePredictGoalAchievement(client)} isAnalyzing={isAnalyzing && selectedClient?.id === client?.id} />
            ))}
          </div>

          {/* Prediction Results */}
          {predictions && selectedClient && (
            <div className="rounded-xl p-6 mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(170,255,0,0.2)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(170,255,0,0.1)' }}>
                    <Icon name="Target" size={20} style={{ color: '#AAFF00' }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold" style={{ color: '#E8EAED' }}>Goal Achievement Prediction: {selectedClient?.fullName}</h2>
                    <p className="text-sm" style={{ color: '#6B7280' }}>AI-powered success forecasting</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" iconName="MessageCircle" onClick={() => handleMessageClient(selectedClient?.id)}>Message Client</Button>
              </div>
              <div className="whitespace-pre-line" style={{ color: '#6B7280' }}>
                {predictions?.split('\n\n')?.map((section, idx) => <p key={idx} className="mb-3">{section}</p>)}
              </div>
            </div>
          )}

          {/* Risk Alert Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {enrichedClients?.filter(c => c?.stats?.adherenceRate < 70 || c?.stats?.missedWorkoutStreak > 2)?.slice(0, 6)?.map(client => (
              <RiskAlertCard key={client?.id} client={client} onGenerateIntervention={() => handleGenerateIntervention(client)} onMessageClient={() => handleMessageClient(client?.id)} />
            ))}
          </div>

          {interventions && selectedClient && (
            <InterventionPanel client={selectedClient} interventions={interventions} onMessageClient={() => handleMessageClient(selectedClient?.id)} onClose={() => { setInterventions(null); setSelectedClient(null); }} />
          )}

          <PredictiveTrendChart clients={enrichedClients} workouts={filteredWorkouts} dateRange={dateRange} />

          {/* Client List */}
          <div className="rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#E8EAED' }}>All Clients - Quick Actions</h2>
            <div className="space-y-3">
              {enrichedClients?.map(client => {
                const stats = client?.stats;
                const riskLevel = stats?.adherenceRate < 40 ? 'high' : stats?.adherenceRate < 70 ? 'medium' : 'low';
                const riskColor = riskLevel === 'high' ? '#FF4444' : riskLevel === 'medium' ? '#FF6B00' : '#AAFF00';
                return (
                  <div key={client?.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-lg gap-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${riskColor}15`, border: `1px solid ${riskColor}30` }}>
                        <span className="text-sm font-bold" style={{ color: riskColor }}>{client?.fullName?.split(' ')?.map(n => n?.[0])?.join('')}</span>
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: '#E8EAED' }}>{client?.fullName}</p>
                        <div className="flex items-center gap-3 text-sm mt-1" style={{ color: '#6B7280' }}>
                          <span style={{ color: riskColor, fontFamily: "'Inter Tight', sans-serif", fontWeight: 700 }}>{stats?.adherenceRate}%</span>
                          <span>adherence</span>
                          <span>•</span>
                          <span className="capitalize">{stats?.adherenceTrend} trend</span>
                          {stats?.missedWorkoutStreak > 0 && <><span>•</span><span style={{ color: '#FF6B00' }}>{stats?.missedWorkoutStreak} missed in a row</span></>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" iconName="Target" onClick={() => handlePredictGoalAchievement(client)} disabled={isAnalyzing}>Predict</Button>
                      <Button variant="outline" size="sm" iconName="Lightbulb" onClick={() => handleGenerateIntervention(client)} disabled={isAnalyzing}>Intervene</Button>
                      <Button variant="outline" size="sm" iconName="MessageCircle" onClick={() => handleMessageClient(client?.id)}>Message</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PredictiveAnalyticsDashboard;